// Local state of the item wizard (one card being created/edited on the items
// board) + pure mappers to/from the restaurant-inquiries API payloads. The
// draft's field naming mirrors the retired grid RowState (brandOS = "open to
// switch brand") so the commercial semantics stay recognizable.

import type { MasterProduct } from 'apis/master-products';
import {
  AcceptableAlternatives,
  CreateRestaurantInquiryItem,
  PackagingSpec,
  RestaurantInquiryItem,
  UpdateRestaurantInquiryItemPayload,
  VolumePeriod,
} from 'apis/restaurant-inquiries';
import { isEmptySpec, packagingSpecKey, packagingSpecOf } from 'pages/inquiry-processor/utils/packaging-spec';
import { AttrSelections, attrValue, ELIMINATION_ATTRS } from 'pages/inquiry-processor/utils/variant-elimination';

export interface WizardDraft {
  familyId?: number;
  familyName: string;
  sel: AttrSelections;
  masterProductId?: number;
  volume: string;
  currentPrice: string;
  targetPrice: string;
  brandOS: boolean;
  originOS: boolean;
  stateOS: boolean;
  specOS: boolean;
  // Pack format flexibility — itemUnit/netContent/packUnit/packSize together.
  // baseUnit is never flexible (see utils/packaging-spec).
  packagingOS: boolean;
  // Per-attribute acceptable alternatives (see AcceptableAlternatives) —
  // only meaningful for attributes whose open-to-switch flag is on.
  alt: AcceptableAlternatives;
  notes: string;
  // Urgency signals: when the current stock expires (YYYY-MM-DD from the
  // native date input, '' = unset) and whether it's already near expiry.
  expirationDate: string;
  nearExpiry: boolean;
}

export function makeDraft(): WizardDraft {
  return {
    familyName: '',
    sel: {},
    volume: '',
    currentPrice: '',
    targetPrice: '',
    // All four flags default to open-to-switch (product feedback 2026-07-13:
    // state used to default FIXED, which read as inconsistent) — ops opts OUT
    // of flexibility explicitly. Matches the BE default of true for absent flags.
    brandOS: true,
    originOS: true,
    stateOS: true,
    specOS: true,
    packagingOS: true,
    alt: {},
    notes: '',
    expirationDate: '',
    nearExpiry: false,
  };
}

// Seed a draft from an existing item + its FULL master product (the item's
// nested summary lacks familyId, so edit mode fetches the product first).
export function draftFromItem(item: RestaurantInquiryItem, product: MasterProduct): WizardDraft {
  const sel: AttrSelections = {};
  ELIMINATION_ATTRS.forEach((k) => {
    sel[k] = attrValue(product, k);
  });
  return {
    familyId: product.familyId,
    familyName: product.name,
    sel,
    masterProductId: product.id,
    volume: String(item.volume ?? ''),
    currentPrice: item.currentPrice != null ? String(item.currentPrice) : '',
    targetPrice: item.expectedPrice != null ? String(item.expectedPrice) : '',
    brandOS: item.brandFlexible,
    originOS: item.originFlexible,
    // Optional on older items; the backend default for absent flags is true.
    stateOS: item.stateFlexible ?? true,
    specOS: item.specFlexible ?? true,
    packagingOS: item.packagingFlexible ?? true,
    alt: normalizeAlt(item.acceptableAlternatives),
    notes: item.notes || '',
    // The BE may serialize the DATEONLY as a full ISO timestamp — the date
    // input needs the bare YYYY-MM-DD either way.
    expirationDate: item.expirationDate ? item.expirationDate.slice(0, 10) : '',
    nearExpiry: item.isNearExpiry ?? false,
  };
}

// '' and non-numeric leftovers like a lone '.' both mean "no price".
const parsePrice = (v: string): number | null => {
  const n = Number(v);
  return v !== '' && Number.isFinite(n) ? n : null;
};

// Attribute ↔ open-to-switch flag pairs that can carry acceptable alternatives.
export const ALT_ATTRS = [
  { attr: 'brand', flag: 'brandOS' },
  { attr: 'origin', flag: 'originOS' },
  { attr: 'state', flag: 'stateOS' },
  { attr: 'spec', flag: 'specOS' },
] as const;

// The resolved variant's own pack format, read out of the draft's string
// selections (sel holds every elimination attribute as a string).
export const draftPackagingSpec = (draft: WizardDraft): PackagingSpec =>
  packagingSpecOf({
    itemUnit: draft.sel.itemUnit,
    netContent: draft.sel.netContent ? Number(draft.sel.netContent) : null,
    packUnit: draft.sel.packUnit,
    packSize: draft.sel.packSize ? Number(draft.sel.packSize) : null,
  });

// Normalized, empties dropped — shared by normalizeAlt and buildAlternatives.
const normalizePackaging = (list?: PackagingSpec[] | null): PackagingSpec[] =>
  (list ?? []).map(packagingSpecOf).filter((spec) => !isEmptySpec(spec));

// Drop empty lists so {} and { brand: [] } compare and serialize identically.
const normalizeAlt = (alt?: AcceptableAlternatives | null): AcceptableAlternatives => {
  const out: AcceptableAlternatives = {};
  ALT_ATTRS.forEach(({ attr }) => {
    const list = alt?.[attr];
    if (list && list.length) out[attr] = [...list];
  });
  const packaging = normalizePackaging(alt?.packaging);
  if (packaging.length) out.packaging = packaging;
  return out;
};

// Order-insensitive fingerprint for change detection.
const altKey = (alt?: AcceptableAlternatives | null): string => {
  const strings = ALT_ATTRS.map(({ attr }) => {
    const list = alt?.[attr];
    return list && list.length ? [...list].sort().join(',') : '*';
  });
  const packaging = normalizePackaging(alt?.packaging).map(packagingSpecKey).sort();
  return [...strings, packaging.length ? packaging.join(',') : '*'].join('|');
};

// Payload form of the draft's alternatives: flagged-on attributes only, minus
// empties and the current variant's own value (that's not an alternative),
// deduped. undefined when nothing survives — the field is omitted entirely.
export function buildAlternatives(draft: WizardDraft): AcceptableAlternatives | undefined {
  const out: AcceptableAlternatives = {};
  ALT_ATTRS.forEach(({ attr, flag }) => {
    if (!draft[flag]) return;
    const current = draft.sel[attr] ?? '';
    const list = Array.from(new Set((draft.alt[attr] ?? []).filter((v) => v && v !== current)));
    if (list.length) out[attr] = list;
  });
  if (draft.packagingOS) {
    // Same pruning as the string attributes: the variant's own format is not an
    // alternative, an all-unset spec would match everything, and case-only
    // duplicates are one format.
    const currentKey = packagingSpecKey(draftPackagingSpec(draft));
    const seen = new Set<string>();
    const packaging = normalizePackaging(draft.alt.packaging).filter((spec) => {
      const key = packagingSpecKey(spec);
      if (key === currentKey || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (packaging.length) out.packaging = packaging;
  }
  return Object.keys(out).length ? out : undefined;
}

// Save gate for the single-screen form: a resolved variant + a positive volume.
export function draftValid(draft: WizardDraft): boolean {
  return !!draft.masterProductId && Number(draft.volume) > 0;
}

export function draftToCreateItem(draft: WizardDraft): CreateRestaurantInquiryItem {
  const item: CreateRestaurantInquiryItem = {
    masterProductId: draft.masterProductId as number,
    volume: Number(draft.volume),
    // The wizard's Volume input asks for the per-order quantity — the stored
    // period must say so too (it read MONTHLY until 2026-07-15).
    volumePeriod: VolumePeriod.PER_ORDER,
    currency: 'AED',
    brandFlexible: draft.brandOS,
    originFlexible: draft.originOS,
    stateFlexible: draft.stateOS,
    specFlexible: draft.specOS,
    packagingFlexible: draft.packagingOS,
  };
  const currentPrice = parsePrice(draft.currentPrice);
  if (currentPrice !== null) item.currentPrice = currentPrice;
  const targetPrice = parsePrice(draft.targetPrice);
  if (targetPrice !== null) item.expectedPrice = targetPrice;
  const alt = buildAlternatives(draft);
  if (alt) item.acceptableAlternatives = alt;
  if (draft.notes.trim()) item.notes = draft.notes.trim();
  if (draft.expirationDate) item.expirationDate = draft.expirationDate;
  if (draft.nearExpiry) item.isNearExpiry = true;
  return item;
}

// True PATCH: only changed fields; cleared prices become explicit nulls.
export function draftToUpdatePayload(
  draft: WizardDraft,
  item: RestaurantInquiryItem
): UpdateRestaurantInquiryItemPayload {
  const data: UpdateRestaurantInquiryItemPayload = {};
  if (draft.masterProductId && draft.masterProductId !== item.masterProductId) {
    data.masterProductId = draft.masterProductId;
  }
  if (Number(draft.volume) !== item.volume) {
    data.volume = Number(draft.volume);
    // A changed volume was typed under the per-order label, so a legacy
    // period (e.g. MONTHLY from pre-2026-07-15 items) is re-stamped with it.
    // Untouched volumes keep their stored period.
    if (item.volumePeriod !== VolumePeriod.PER_ORDER) data.volumePeriod = VolumePeriod.PER_ORDER;
  }
  const curr = parsePrice(draft.currentPrice);
  if (curr !== (item.currentPrice ?? null)) data.currentPrice = curr;
  const exp = parsePrice(draft.targetPrice);
  if (exp !== (item.expectedPrice ?? null)) data.expectedPrice = exp;
  if (draft.brandOS !== item.brandFlexible) data.brandFlexible = draft.brandOS;
  if (draft.originOS !== item.originFlexible) data.originFlexible = draft.originOS;
  if (draft.stateOS !== (item.stateFlexible ?? true)) data.stateFlexible = draft.stateOS;
  if (draft.specOS !== (item.specFlexible ?? true)) data.specFlexible = draft.specOS;
  if (draft.packagingOS !== (item.packagingFlexible ?? true)) data.packagingFlexible = draft.packagingOS;
  const alt = buildAlternatives(draft);
  if (altKey(alt) !== altKey(normalizeAlt(item.acceptableAlternatives))) {
    data.acceptableAlternatives = alt ?? null;
  }
  if (draft.notes.trim() !== (item.notes || '')) data.notes = draft.notes.trim();
  // A cleared date becomes an explicit null, like the prices.
  if (draft.expirationDate !== (item.expirationDate?.slice(0, 10) ?? '')) {
    data.expirationDate = draft.expirationDate || null;
  }
  if (draft.nearExpiry !== (item.isNearExpiry ?? false)) data.isNearExpiry = draft.nearExpiry;
  return data;
}

export { numOnly } from 'pages/inquiry-processor/utils/num-only';
